/**
 * Formats a session's start/end time as e.g. "1:00-1:50 PM EDT", collapsing a
 * shared AM/PM suffix (e.g. "10:00-10:45 AM") and appending the viewer's
 * timezone abbreviation. Returns null when either timestamp is missing or
 * invalid so callers can omit the time range rather than show a placeholder.
 */
export function formatSessionTimeRange(startAt?: string, endAt?: string): string | null {
  if (!startAt || !endAt) {
    return null;
  }

  const start = new Date(startAt);
  const end = new Date(endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  };
  const startTime = start.toLocaleTimeString('en', options);
  const endTime = end.toLocaleTimeString('en', options);
  const startMeridiem = startTime.slice(-2);
  const endMeridiem = endTime.slice(-2);
  const timeZoneName = new Intl.DateTimeFormat('en', { timeZone, timeZoneName: 'short' })
    .formatToParts(start)
    .find((part) => part.type === 'timeZoneName')?.value;

  const range = `${
    startMeridiem === endMeridiem ? startTime.replace(/\s?[AP]M$/u, '') : startTime
  }-${endTime}`;
  return timeZoneName ? `${range} ${timeZoneName}` : range;
}
